/**
 * The web's permission flow: never on load. The first notification that
 * can't be shown because the browser was never asked opens a glass bar in
 * the app that says why (NotifyAskBar); only its "Turn on" asks the browser
 * (a click, which browsers require). "Not now" waits a week before asking
 * again. The desktop app needs none of this: the shell shows native toasts.
 */
import { create } from 'zustand';
import { desktopBridge } from '../lib/config';
import { storage } from '../lib/storage';

export type Permission = NotificationPermission | 'unsupported';

export const ASK_AGAIN_MS = 7 * 24 * 3600 * 1000;
const LATER_KEY = 'notify-ask-later';

export const useNotifyAsk = create<{ open: boolean }>(() => ({ open: false }));

export function notificationPermission(): Permission {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

/** A notification could not be shown for want of a permission: offer to ask, if that is due. */
export function offerPermission(): void {
  if (desktopBridge() || notificationPermission() !== 'default') return;
  if (Date.now() < Number(storage.get(LATER_KEY) ?? 0)) return;
  if (!useNotifyAsk.getState().open) useNotifyAsk.setState({ open: true });
}

/** "Not now": the bar closes and comes back in a week at the earliest. */
export function askLater(): void {
  storage.set(LATER_KEY, String(Date.now() + ASK_AGAIN_MS));
  useNotifyAsk.setState({ open: false });
}

/** "Turn on": the browser's own prompt. */
export async function askBrowser(): Promise<Permission> {
  if (typeof Notification === 'undefined') return 'unsupported';
  let r: Permission;
  try {
    r = await Notification.requestPermission();
  } catch {
    r = notificationPermission();
  }
  if (r === 'default') askLater();
  else useNotifyAsk.setState({ open: false });
  return r;
}
